import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { ScanLine, IdCard, RefreshCw, Check, X } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => d.split("-").reverse().slice(0, 2).join("/");

export default function DailyAttendanceTab({ trainingId }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/trainings/${trainingId}/daily-attendance`);
    setData(r.data);
  }, [trainingId]);
  useEffect(() => { load(); }, [load]);

  const undo = async (pid, date) => {
    await api.delete(`/trainings/${trainingId}/daily-checkin/${pid}/${date}`);
    toast.success("Presença removida");
    load();
  };

  if (!data) return <p className="text-xs text-muted-foreground">Carregando…</p>;
  const days = data.days.includes(data.today) ? data.days : [...data.days, data.today];
  const presentToday = data.participants.filter((p) => p.days[data.today]).length;

  return (
    <div className="space-y-4">
      <Card className="p-6 shadow-none border-border flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="overline">Presença diária por crachá</p>
          <p className="text-sm text-muted-foreground mt-1">A equipe lê o QR do crachá com o celular e a presença do dia é registrada.</p>
          <p className="text-xs mt-2" data-testid="daily-today-summary">Hoje ({fmt(data.today)}): <span className="text-[#D4AF37] font-semibold">{presentToday}</span> de {data.participants.length} presentes</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={load} data-testid="daily-refresh-btn"><RefreshCw size={14} className="mr-1" /> Atualizar</Button>
          <Button variant="outline" asChild data-testid="daily-badges-btn">
            <a href={`${BACKEND_URL}/api/trainings/${trainingId}/badges/pdf`} target="_blank" rel="noreferrer"><IdCard size={14} className="mr-1" /> Crachás PDF</a>
          </Button>
          <Button className="bg-[#C07C88] hover:bg-[#A86571]" asChild data-testid="daily-open-scanner-btn">
            <Link to={`/trainings/${trainingId}/scanner`}><ScanLine size={14} className="mr-1" /> Abrir leitor QR</Link>
          </Button>
        </div>
      </Card>
      <Card className="p-6 shadow-none border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="daily-matrix">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="py-2 pr-4">Participante</th>
              {days.map((d) => <th key={d} className="py-2 px-2 text-center">{fmt(d)}</th>)}
              <th className="py-2 pl-2 text-center">Dias</th>
            </tr>
          </thead>
          <tbody>
            {data.participants.map((p) => (
              <tr key={p.participant_id} className="border-t border-border" data-testid={`daily-row-${p.participant_id}`}>
                <td className="py-2 pr-4 flex items-center gap-3 min-w-[220px]">
                  {p.has_photo
                    ? <img src={`${BACKEND_URL}/api/trainings/${trainingId}/participants/${p.participant_id}/photo`} alt="" className="w-8 h-8 rounded-full object-cover" />
                    : <span className="w-8 h-8 rounded-full bg-[#F7EDEF] text-[#C07C88] text-xs font-semibold flex items-center justify-center">{p.name.slice(0, 1).toUpperCase()}</span>}
                  <span className="truncate">{p.name}</span>
                </td>
                {days.map((d) => (
                  <td key={d} className="py-2 px-2 text-center">
                    {p.days[d] ? (
                      <button onClick={() => undo(p.participant_id, d)} title={`${p.days[d].slice(11, 16)} UTC — clique para remover`} className="inline-flex w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 items-center justify-center hover:bg-red-100 hover:text-red-600 transition-colors" data-testid={`daily-cell-${p.participant_id}-${d}`}><Check size={13} /></button>
                    ) : <X size={13} className="inline text-muted-foreground/40" />}
                  </td>
                ))}
                <td className="py-2 pl-2 text-center font-semibold text-[#D4AF37]">{p.days_present}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.participants.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Sem participantes inscritos</p>}
      </Card>
    </div>
  );
}
